import axios from "axios";
import Constant from "utils/Constants";
import LocalStorageUtils from "utils/LocalStorage";
import { dismissAllToast, errorToast } from "utils/Toast";

export const AXIOS_ABORT_CONTROLLER = new AbortController();

let isLoggingOut = false;

// Temporary UI-demo setting. Keep 401 handling active without displaying
// Unauthorized notifications while backend permissions are incomplete.
const SHOW_UNAUTHORIZED_TOAST = false;

const api = axios.create({
  baseURL: Constant.baseApiURL,
  withCredentials: true,
  signal: AXIOS_ABORT_CONTROLLER.signal,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

// ✅ Request interceptor (inject token)
api.interceptors.request.use(
  (config) => {
    const token = LocalStorageUtils.jwtToken.get();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ✅ Response interceptor (handle 401, 500, etc.)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const message = error?.response?.data?.message;

    // 🔴 Unauthorized (InvalidToken)
    if (status === 401) {
      const isInvalidToken = message === "InvalidToken";
      if (SHOW_UNAUTHORIZED_TOAST) {
        dismissAllToast();
        errorToast(`${message || "Unauthorized"}.`);
      }

      if (isInvalidToken && !isLoggingOut) {
        isLoggingOut = true;
        try {
          const revoke = await api.post("/User/revoke-cookies");
          if (revoke?.data?.isValid && revoke?.data?.statusCode === 200) {
            setTimeout(() => {
              LocalStorageUtils.all.clear();
              window.location.replace("/");
            }, 1000);
          } else {
            errorToast("Error revoke cookies");
          }
        } catch (logoutError) {
          console.error("Error during revoke-cookies:", logoutError);
        } finally {
          isLoggingOut = false;
        }
      }

      return Promise.resolve(false);
    }

    // 🔴 Internal Server Error
    if (status === 500) {
      dismissAllToast();
      errorToast(error?.response?.data?.message || "Internal Server Error");
      return Promise.resolve(false);
    }

    // 🔴 Default error handler
    return Promise.reject(error);
  }
);

export default api;
